import React, { FC, Fragment, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { Button, Card, CardContent, Grid, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PublishIcon from '@material-ui/icons/Publish';
import classNames from 'classnames';

import { Dimensions, IUploader, Loaders, Uploader, UploadInput } from 'components';
import { getEmbed, getPhoto, isEmbed } from 'features/content/helpers';
import { Image, Photo, Title } from 'features/content/widgets';
import { ContentInput, PreviewUrlMutation, UrlMetadataImage, usePreviewUrlMutation } from 'api';
import { useNotify } from 'hooks';
import { DimensionsProvider } from 'providers';
import { getImageSrc } from 'helpers';
import { queryString } from 'utilities';
import { USER_FEED_ID } from 'constants/index';

import styles from '../../../posting/components/PreviewPartials/styles';

const { REACT_APP_IMGIX_HOST } = process.env;

const useStyles = makeStyles(styles);

interface IProps {
  url: string;
}

const UrlPreview: FC<IProps> = ({ url }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const notify = useNotify();
  const { values, setFieldValue } = useFormikContext<ContentInput>();

  useEffect(() => {
    const groupId = USER_FEED_ID;

    previewUrlMutation({
      variables: { url, group_id: groupId },
    });
  }, [url]);

  const [previewUrlMutation, { loading, error }] = usePreviewUrlMutation({
    onError: notify.mutationError,
    onCompleted: mutation => handleCompleted(mutation),
  });

  function handleCompleted(mutation: PreviewUrlMutation) {
    const { is_engageable, is_shareable, content_object } = mutation.processContent;

    setFieldValue('content_object.url', content_object.url);
    setFieldValue('content_object.url_metadata', content_object.url_metadata);
    setFieldValue('content_object.tweet_metadata', content_object.tweet_metadata);
    setFieldValue('content_object.youtube_metadata', content_object.youtube_metadata);
    setFieldValue('content_object.instagram_metadata', content_object.instagram_metadata);
    setFieldValue('content_object.reddit_metadata', content_object.reddit_metadata);

    setFieldValue('is_engageable', is_engageable);
    setFieldValue('is_shareable', is_shareable);
  }

  function onSaveUploader() {
    console.log("uploader")
  }

  function renderThumbnails(images: UrlMetadataImage[]) {
    return images.map((image, idx) => {
      const params = {
        fit: 'fill',
        fill: 'solid',
        h: 72,
        w: 90,
        auto: 'format'
      };

      const imageUrl = `${REACT_APP_IMGIX_HOST}/${image.file_key}?${queryString(params)}`;
      const imageSrc = getImageSrc(imageUrl);

      return (
        <Grid item key={idx}>
          <img {...imageSrc} />
        </Grid>
      )
    })
  }

  function renderContent() {
    if (isEmbed(values)) {
      return <div className={classes.align}>{getEmbed(values)}</div>;
    }

    const { content_object } = values;
    let title = null;
    let description = null;
    let images = null;
    if (content_object.url_metadata) {
      title = content_object.url_metadata.title;
      description = content_object.url_metadata.description;
      images = content_object.url_metadata.images;
    }

    const photo = getPhoto(values);

    return (
      <Fragment>
        <Dimensions>
          {dimensions => (
            <DimensionsProvider value={{ dimensions }}>
              {!!photo ? <Photo photo={photo} /> : <Image content={values} />}
            </DimensionsProvider>
          )}
        </Dimensions>
        <CardContent classes={{ root: classes.chooseImage }}>
          <Typography color="textSecondary" className={classes.chooseImageTitle}>{t('posting:chooseImage')}</Typography>
          <Grid container spacing={1}>
            {!!images && renderThumbnails(images)}
            <Grid item>
              <Uploader onSave={onSaveUploader} disableCropper>
                {(uploaderProps: IUploader) => (
                  <UploadInput onUpload={uploaderProps.onUpload}>
                    <Button component="span" classes={{ root: classes.uploadButton, label: classes.buttonLabel }}>
                      <PublishIcon />
                      <Typography className={classes.uploadButtonText}>{t('posting:upload')}</Typography>
                    </Button>
                  </UploadInput>
                )}
              </Uploader>
            </Grid>
          </Grid>
        </CardContent>
        <CardContent classes={{ root: classes.cardBody }}>
          <Title title={title} />
          <Typography color="textSecondary">{description}</Typography>
        </CardContent>
      </Fragment>
    );
  }

  if (error) {
    return (
      <Card className={classNames(classes.card, classes.cardPaper)} square>
        <div className={classes.unavailable}>
          <Typography>
            <Trans i18nKey="posting:previewUnavailable" />
          </Typography>
        </div>
      </Card>
    )
  }

  return (
    <Fragment>
      {loading && <Loaders.Standard />}
      {!loading && values && (
        <Paper className={classes.cardPaper}>
          <Card elevation={0} className={classes.card} square>
            {renderContent()}
          </Card>
        </Paper>
      )}
    </Fragment>
  );
};

export default UrlPreview;
